import React, { useMemo } from "react";
import { Box, Card, CardContent, Paper, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import moment from "moment";
import { formatDateTime, formatMinutesToHours } from "../../helper/global";

const SLEEP_METRICS = [
    { key: "awakeMinutes", label: "Awake", color: "#F9A825" },
    { key: "coreMinutes", label: "Core", color: "#4CAF50" },
    { key: "deepMinutes", label: "Deep", color: "#1E88E5" },
    { key: "remMinutes", label: "REM", color: "#8E24AA" },
    //incldue inBedMinutes
    { key: "inBedMinutes", label: "In Bed", color: "#FF7043" },
];

export default function SleepDataChart({ theme, healthData = [] }) {
    const sortedData = useMemo(() => {
        return [...(healthData || [])]
            .filter(
                (entry) =>
                    entry?.breakdown && typeof entry.breakdown === "object",
            )
            .map((entry) => {
                const breakdown = entry.breakdown;
                const date =
                    breakdown?.startDate ||
                    entry?.date ||
                    entry?.startDate ||
                    null;
                return { ...entry, breakdown, chartDate: date };
            })
            .filter((entry) => entry.chartDate)
            .sort(
                (a, b) =>
                    moment(a.chartDate).valueOf() -
                    moment(b.chartDate).valueOf(),
            );
    }, [healthData]);

    const xLabels = sortedData.map((entry) =>
        formatDateTime(entry.chartDate, "MMM D, YYYY", false),
    );

    const series = SLEEP_METRICS.map((metric) => ({
        id: metric.key,
        data: sortedData.map((entry) => {
            const value = Number(entry.breakdown?.[metric.key]);
            return Number.isFinite(value) ? value : 0;
        }),
        label: metric.label,
        stack: "sleep",
        color: metric.color,
    }));

    const ItemTooltipContent = ({ itemData }) => {
        const dataIndex = itemData?.dataIndex;
        if (dataIndex == null) {
            return null;
        }

        const selectedKey = itemData?.seriesId;
        const lineColor = (key) =>
            selectedKey && key === selectedKey
                ? theme.palette.text.primary
                : theme.palette.text.secondary;

        const entry = sortedData[dataIndex];
        if (!entry) {
            return null;
        }

        const asleepMinutes = Number(entry.breakdown?.timeAsleepMinutes);
        const asleepText = Number.isFinite(asleepMinutes)
            ? formatMinutesToHours(asleepMinutes)
            : "0m";
        const score = Number(entry.breakdown?.sleepScore);
        const scoreText = Number.isFinite(score) ? Math.round(score) : 0;

        const startDate = entry.breakdown?.startDate;
        const endDate = entry.breakdown?.endDate;
        const dateRange =
            startDate && endDate
                ? `${formatDateTime(startDate, "MMM D h:mm A", false)} - ${formatDateTime(endDate, "MMM D h:mm A", false)}`
                : formatDateTime(entry.chartDate, "MMM D, YYYY", false);

        const inBedMinutes = Number(entry.breakdown?.inBedMinutes);
        const inBedText = Number.isFinite(inBedMinutes)
            ? formatMinutesToHours(inBedMinutes)
            : "0m";
        const coreMinutes = Number(entry.breakdown?.coreMinutes);
        const coreText = Number.isFinite(coreMinutes)
            ? formatMinutesToHours(coreMinutes)
            : "0m";
        const remMinutes = Number(entry.breakdown?.remMinutes);
        const remText = Number.isFinite(remMinutes)
            ? formatMinutesToHours(remMinutes)
            : "0m";
        const deepMinutes = Number(entry.breakdown?.deepMinutes);
        const deepText = Number.isFinite(deepMinutes)
            ? formatMinutesToHours(deepMinutes)
            : "0m";
        const awakeMinutes = Number(entry.breakdown?.awakeMinutes);
        const awakeText = Number.isFinite(awakeMinutes)
            ? formatMinutesToHours(awakeMinutes)
            : "0m";

        return (
            <Paper elevation={3} sx={{ p: 1.5 }}>
                <Typography variant="subtitle2">{dateRange}</Typography>
                <Typography variant="h5">
                    {`${asleepText} (${scoreText} pts)`}
                </Typography>
                <Box sx={{ mt: 0 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        {`In Bed: ${inBedText}`}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        {`Core: ${coreText}`}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        {`REM: ${remText}`}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        {`Deep: ${deepText}`}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        {`Awake: ${awakeText}`}
                    </Typography>
                </Box>
            </Paper>
        );
    };

    return (
        <Card>
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography variant="h6">Sleep Metrics</Typography>
                </Box>
                <Box sx={{ width: "100%" }}>
                    <BarChart
                        series={series}
                        tooltip={{
                            trigger: "item",
                        }}
                        slots={{ itemContent: ItemTooltipContent }}
                        slotProps={{
                            legend: {
                                position: {
                                    vertical: "bottom",
                                    horizontal: "middle",
                                },
                                direction: "row",
                                padding: { top: 8 },
                            },
                        }}
                        xAxis={[
                            {
                                data: xLabels,
                                scaleType: "band",
                                tickLabelStyle: {
                                    angle: 0,
                                    textAnchor: "start",
                                    fontSize: 11,
                                },
                                valueFormatter: (date) => {
                                    const split = date.split(", ");
                                    return `${split[0]}\n${split[1]}`;
                                },
                                tickInterval: (value, index) => {
                                    if (xLabels.length <= 15) {
                                        return true;
                                    }

                                    if (xLabels.length <= 50) {
                                        return index % 5 === 0;
                                    }

                                    if (xLabels.length <= 100) {
                                        return index % 10 === 0;
                                    }

                                    return index % 15 === 0;
                                },
                            },
                        ]}
                        height={300}
                        margin={{ left: 50, right: 50, top: 20, bottom: 90 }}
                    />
                </Box>
            </CardContent>
        </Card>
    );
}
